// src/features/ManageCMDBDiscoveredAppsHeader/DisableCMDBDiscoveredAppsModal/DisableCMDBDiscoveredAppsModal.tsx
import React from 'react';
import { Button, ModalIsland } from '@saviynt/design-system';

import useLocalization from '../../hooks/use-localize';

import { usePatchDiscoveryStatusMutation } from './data-store/changeDiscoverAppStatusSlice';

interface DisableCMDBDiscoveredAppsModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  id: number;
  name: string;
  type: string;
  status: string;

  changeStatusCallback: (
    resp: { data?: unknown; error?: unknown },
    status: 'enable' | 'disable'
  ) => void;
}

/**
 * Modal to confirm disabling CMDB discovered apps (e.g., ServiceNow).
 * Controlled by parent via `isOpen` and `setIsOpen` props.
 */
function DisableCMDBDiscoveredAppsModal({
  isOpen,
  setIsOpen,
  id,
  name,
  type,
  status,
  changeStatusCallback,
}: DisableCMDBDiscoveredAppsModalProps) {
  const formatMessage = useLocalization();

  const [postStatus, { isLoading: isStatusChangeLoading }] =
    usePatchDiscoveryStatusMutation();

  const handleDisable = () => {
    postStatus({ id, type, status })
      .then((resp: { data?: unknown; error?: unknown }) => {
        changeStatusCallback(resp, 'disable');
      })
      .catch((error: unknown) => {
        changeStatusCallback({ error }, 'disable');
      });
  };

  return (
    <ModalIsland
      kind='warning'
      alertTitle={`${formatMessage(
        'INTEGRATIONS.DISABLE_MODAL.ALERT_TITLE',
        'Are you sure you want to disable '
      )} ${name}?`}
      alertSubtitle={formatMessage(
        'INTEGRATIONS.DISABLE_MODAL.ALERT_SUBTITLE',
        'By disabling the discovery source, asset import will be stopped and you won’t be able to configure the source.'
      )}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      primaryButton={
        <Button
          type='button'
          kind='outlined'
          size='medium'
          isLoading={isStatusChangeLoading}
          loaderText={formatMessage(
            'INTEGRATIONS.BUTTON.DISABLING',
            'Disabling'
          )}
          onClick={handleDisable}>
          {formatMessage('INTEGRATIONS.BUTTON.DISABLE', 'Disable')}
        </Button>
      }
      secondaryButton={
        <Button
          type='button'
          kind='ghost'
          size='medium'
          onClick={() => setIsOpen(false)}>
          {formatMessage('INTEGRATIONS.BUTTON.CANCEL', 'Cancel')}
        </Button>
      }
    />
  );
}

export default React.memo(DisableCMDBDiscoveredAppsModal);
